import { useEffect, useMemo, useState } from 'react';

import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import SearchIcon from '@mui/icons-material/Search';
import {
    Alert,
    Button,
    CircularProgress,
    MenuItem,
    Paper,
    Stack,
    TextField,
    Typography,
} from '@mui/material';
import { useNotify } from 'react-admin';

import {
    assignAdminIndividualSubscriptionToUser,
    getAdminUserActiveSubscription,
    type AdminUserActiveSubscription,
} from '../admin/adminSubscriptionDataProvider';
import { formatApiDateTime } from '../admin/dateTimeUtils';
import type { BillingCountryCurrency, BillingPlan } from './billingData';
import { getStartingPlanPrice } from './billingUtils';

type UserSubscriptionActivationSectionProps = {
    countryCurrencies: BillingCountryCurrency[];
    plans: BillingPlan[];
};

function formatDateTime(value: string): string {
    return formatApiDateTime(value);
}

function getPlanStatusLabel(plan: BillingPlan): string {
    return plan.isActive ? 'Active plan' : 'Inactive plan';
}

export function UserSubscriptionActivationSection({
    countryCurrencies,
    plans,
}: UserSubscriptionActivationSectionProps) {
    const notify = useNotify();
    const [userIdDraft, setUserIdDraft] = useState('');
    const [selectedPlanId, setSelectedPlanId] = useState('');
    const [lookupUserId, setLookupUserId] = useState('');
    const [activeSubscription, setActiveSubscription] = useState<AdminUserActiveSubscription | null>(null);
    const [lookupPerformed, setLookupPerformed] = useState(false);
    const [isLookupLoading, setIsLookupLoading] = useState(false);
    const [isAssigning, setIsAssigning] = useState(false);
    const [errorMessage, setErrorMessage] = useState('');
    const [infoMessage, setInfoMessage] = useState('');
    const [successMessage, setSuccessMessage] = useState('');

    const sortedPlans = useMemo(
        () =>
            [...plans].sort((left, right) => {
                if (left.isActive !== right.isActive) {
                    return left.isActive ? -1 : 1;
                }

                return left.name.localeCompare(right.name);
            }),
        [plans],
    );

    const selectedPlan = useMemo(
        () => sortedPlans.find((plan) => String(plan.id) === selectedPlanId) ?? null,
        [selectedPlanId, sortedPlans],
    );

    useEffect(() => {
        if (selectedPlanId && sortedPlans.some((plan) => String(plan.id) === selectedPlanId)) {
            return;
        }

        const fallbackPlan = sortedPlans.find((plan) => plan.isActive) ?? sortedPlans[0];
        setSelectedPlanId(fallbackPlan ? String(fallbackPlan.id) : '');
    }, [selectedPlanId, sortedPlans]);

    async function loadActiveSubscription(userId: string, preserveSuccessMessage = false) {
        const normalizedUserId = userId.trim();
        if (!normalizedUserId) {
            setErrorMessage('Enter a user ID before loading the active subscription.');
            setInfoMessage('');
            setSuccessMessage('');
            setLookupPerformed(false);
            setActiveSubscription(null);
            return;
        }

        setIsLookupLoading(true);
        setLookupPerformed(true);
        setLookupUserId(normalizedUserId);
        setErrorMessage('');
        setInfoMessage('');
        if (!preserveSuccessMessage) {
            setSuccessMessage('');
        }

        try {
            const subscription = await getAdminUserActiveSubscription(normalizedUserId);
            setActiveSubscription(subscription);

            if (!subscription) {
                setInfoMessage('No active subscription was found for this user.');
            }
        } catch (error) {
            const message = error instanceof Error ? error.message : 'Unable to load the active subscription';
            setActiveSubscription(null);
            setErrorMessage(message);
            notify(message, { type: 'error' });
        } finally {
            setIsLookupLoading(false);
        }
    }

    async function handleActivateSubscription(event: React.FormEvent<HTMLFormElement>) {
        event.preventDefault();

        const normalizedUserId = userIdDraft.trim();
        if (!normalizedUserId) {
            const message = 'Enter a user ID before activating a subscription.';
            setErrorMessage(message);
            setInfoMessage('');
            setSuccessMessage('');
            notify(message, { type: 'warning' });
            return;
        }

        if (!selectedPlanId) {
            const message = 'Select a plan before activating a subscription.';
            setErrorMessage(message);
            setInfoMessage('');
            setSuccessMessage('');
            notify(message, { type: 'warning' });
            return;
        }

        setIsAssigning(true);
        setErrorMessage('');
        setInfoMessage('');
        setSuccessMessage('');

        try {
            await assignAdminIndividualSubscriptionToUser(normalizedUserId, selectedPlanId);

            const message = selectedPlan
                ? `Activated ${selectedPlan.name} for user ${normalizedUserId}.`
                : `Activated the selected plan for user ${normalizedUserId}.`;

            setSuccessMessage(message);
            notify(message, { type: 'info' });
            await loadActiveSubscription(normalizedUserId, true);
        } catch (error) {
            const message = error instanceof Error ? error.message : 'Unable to activate the subscription';
            setErrorMessage(message);
            notify(message, { type: 'error' });
        } finally {
            setIsAssigning(false);
        }
    }

    const activeSubscriptionPlan =
        sortedPlans.find((plan) => String(plan.id) === activeSubscription?.planId) ?? null;

    return (
        <Stack spacing={3}>
            <Paper variant="outlined" sx={{ p: 2 }}>
                <Stack spacing={1}>
                    <Typography variant="h6">Activate User Subscription</Typography>
                    <Typography variant="body2" color="text.secondary">
                        Manually grant an individual subscription to a user without payment, then verify the currently
                        active plan for that user.
                    </Typography>
                </Stack>
            </Paper>

            <Stack direction={{ xs: 'column', lg: 'row' }} spacing={3} alignItems="stretch">
                <Paper
                    variant="outlined"
                    sx={{ p: 2, flex: 1, display: 'flex', flexDirection: 'column', gap: 2 }}
                    component="form"
                    onSubmit={handleActivateSubscription}
                >
                    <Typography variant="h6">Assign Subscription</Typography>
                    <TextField
                        label="User ID"
                        value={userIdDraft}
                        onChange={(event) => setUserIdDraft(event.target.value)}
                        placeholder="00000000-0000-0000-0000-000000000000"
                        fullWidth
                    />
                    <TextField
                        select
                        label="Plan"
                        value={selectedPlanId}
                        onChange={(event) => setSelectedPlanId(event.target.value)}
                        fullWidth
                        disabled={sortedPlans.length === 0}
                        helperText={
                            selectedPlan
                                ? `${getPlanStatusLabel(selectedPlan)} | ${selectedPlan.durationDays} day duration`
                                : 'Load plans in the billing workspace before assigning a subscription.'
                        }
                    >
                        {sortedPlans.map((plan) => (
                            <MenuItem key={String(plan.id)} value={String(plan.id)}>
                                {plan.name}{plan.isActive ? '' : ' (inactive)'}
                            </MenuItem>
                        ))}
                    </TextField>

                    {selectedPlan ? (
                        <Paper variant="outlined" sx={{ p: 2, bgcolor: 'grey.50' }}>
                            <Stack spacing={0.75}>
                                <Typography variant="subtitle2">Selected Plan Summary</Typography>
                                <Typography variant="body2">Plan ID: {selectedPlan.id}</Typography>
                                <Typography variant="body2">
                                    Duration: {selectedPlan.durationDays} day{selectedPlan.durationDays === 1 ? '' : 's'}
                                </Typography>
                                <Typography variant="body2">
                                    Pricing: {getStartingPlanPrice(selectedPlan, countryCurrencies)}
                                </Typography>
                                {selectedPlan.description ? (
                                    <Typography variant="body2" color="text.secondary">
                                        {selectedPlan.description}
                                    </Typography>
                                ) : null}
                            </Stack>
                        </Paper>
                    ) : null}

                    <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
                        <Button
                            type="submit"
                            variant="contained"
                            startIcon={isAssigning ? <CircularProgress size={16} color="inherit" /> : <CheckCircleOutlineIcon />}
                            disabled={isAssigning || isLookupLoading || sortedPlans.length === 0}
                        >
                            Activate Subscription
                        </Button>
                        <Button
                            variant="outlined"
                            startIcon={isLookupLoading ? <CircularProgress size={16} color="inherit" /> : <SearchIcon />}
                            disabled={isAssigning}
                            onClick={() => void loadActiveSubscription(userIdDraft)}
                        >
                            Load Active Subscription
                        </Button>
                    </Stack>
                </Paper>

                <Paper variant="outlined" sx={{ p: 2, flex: 1, display: 'flex', flexDirection: 'column', gap: 2 }}>
                    <Typography variant="h6">Current Active Subscription</Typography>

                    {lookupPerformed ? (
                        <Typography variant="body2" color="text.secondary">
                            Lookup user: {lookupUserId}
                        </Typography>
                    ) : (
                        <Typography variant="body2" color="text.secondary">
                            Enter a user ID and load the current active subscription.
                        </Typography>
                    )}

                    {isLookupLoading ? (
                        <Stack direction="row" spacing={1} alignItems="center">
                            <CircularProgress size={18} />
                            <Typography variant="body2">Loading active subscription...</Typography>
                        </Stack>
                    ) : null}

                    {activeSubscription ? (
                        <Stack spacing={1.5}>
                            <Paper variant="outlined" sx={{ p: 2, bgcolor: activeSubscription.isActive ? 'success.50' : 'grey.50' }}>
                                <Stack spacing={1}>
                                    <Typography variant="subtitle2">
                                        {activeSubscription.planName || activeSubscriptionPlan?.name || activeSubscription.planId || 'Active plan'}
                                    </Typography>
                                    <Typography variant="body2">Status: {activeSubscription.status || 'Active'}</Typography>
                                    <Typography variant="body2">User ID: {activeSubscription.userId || lookupUserId}</Typography>
                                    <Typography variant="body2">
                                        Plan ID: {activeSubscription.planId || activeSubscriptionPlan?.id || 'N/A'}
                                    </Typography>
                                    <Typography variant="body2">
                                        Starts: {formatDateTime(activeSubscription.startsAt)}
                                    </Typography>
                                    <Typography variant="body2">
                                        Ends: {formatDateTime(activeSubscription.endsAt)}
                                    </Typography>
                                    <Typography variant="body2">
                                        Updated: {formatDateTime(activeSubscription.updatedAt)}
                                    </Typography>
                                </Stack>
                            </Paper>

                            {activeSubscriptionPlan ? (
                                <Paper variant="outlined" sx={{ p: 2 }}>
                                    <Stack spacing={0.75}>
                                        <Typography variant="subtitle2">Matched Billing Plan</Typography>
                                        <Typography variant="body2">
                                            {activeSubscriptionPlan.durationDays} day duration
                                        </Typography>
                                        <Typography variant="body2">
                                            {getStartingPlanPrice(activeSubscriptionPlan, countryCurrencies)}
                                        </Typography>
                                    </Stack>
                                </Paper>
                            ) : null}
                        </Stack>
                    ) : lookupPerformed && !isLookupLoading ? (
                        <Typography variant="body2" color="text.secondary">
                            No active subscription details are currently loaded.
                        </Typography>
                    ) : null}
                </Paper>
            </Stack>

            {successMessage ? <Alert severity="success">{successMessage}</Alert> : null}
            {infoMessage ? <Alert severity="info">{infoMessage}</Alert> : null}
            {errorMessage ? <Alert severity="error">{errorMessage}</Alert> : null}
        </Stack>
    );
}